import React, { useEffect, useState } from "react";
import "./hotelTerms.css";
import usePageMeta from "../../../hooks/usePageMeta";

const hotelTerms = [
  {
    number: 1,
    title: "Hotel Booking",
    paragraphs: [
      "MakeBeeTrip is a B2C travel platform that facilitates hotel bookings through hotels, hotel suppliers, aggregators and other authorized travel service providers.",
      "Hotel bookings are subject to the availability, rates, policies and terms of the respective hotel or accommodation provider.",
    ],
  },
  {
    number: 2,
    title: "Hotel Availability and Rates",
    paragraphs: [
      "All hotel rooms, rates and availability displayed on the website are subject to availability at the time of booking.",
      "Hotel prices may change due to:",
    ],
    bullets: [
      "Room availability",
      "Hotel pricing changes",
      "Seasonal demand",
      "Promotional offers",
      "Occupancy",
      "Taxes and government charges",
      "Changes in booking dates",
      "Changes in number of guests",
      "Changes in room type or meal plan",
    ],
    after: [
      "The final applicable price will be confirmed at the time of booking.",
    ],
  },
  {
    number: 3,
    title: "Booking Confirmation",
    paragraphs: [
      "A hotel booking is considered confirmed only after the booking has been successfully processed and a hotel booking confirmation/voucher has been issued.",
      "Payment confirmation alone does not necessarily guarantee hotel confirmation.",
      "Customers should retain their hotel booking confirmation or voucher and present it at check-in where required.",
    ],
  },
  {
    number: 4,
    title: "Guest Information",
    paragraphs: [
      "Customers must provide accurate information while making a hotel booking.",
      "This may include:",
    ],
    bullets: [
      "Guest name",
      "Mobile number",
      "Email address",
      "Number of adults",
      "Number of children",
      "Children's ages",
      "Check-in date",
      "Check-out date",
      "Other information required by the hotel",
    ],
    after: [
      "Incorrect information may result in additional charges, booking restrictions or inability to provide the booked accommodation.",
    ],
  },
  {
    number: 5,
    title: "Check-In and Check-Out",
    paragraphs: [
      "Standard check-in and check-out times are determined by the respective hotel.",
      "Early check-in or late check-out is subject to hotel availability and may involve additional charges.",
      "Customers should contact the hotel or MakeBeeTrip in advance if they require special check-in or check-out arrangements.",
    ],
  },
  {
    number: 6,
    title: "Hotel Identification and Guest Requirements",
    paragraphs: [
      "Hotels may require guests to present valid identification at check-in.",
      "International guests may be required to provide passport and visa details according to applicable local regulations.",
      "The hotel may refuse check-in if required identification or documentation is not provided.",
    ],
  },
  {
    number: 7,
    title: "Room Type",
    paragraphs: [
      "The room type confirmed in the booking is subject to the specific booking confirmation.",
      "Room images displayed on the website are intended for reference and may vary slightly from the actual room.",
      "Room layouts, furnishings, views and facilities may differ between individual rooms of the same category.",
    ],
  },
  {
    number: 8,
    title: "Occupancy",
    paragraphs: [
      "The maximum number of guests permitted in a room is determined by the hotel.",
      "Customers must provide the correct number of adults and children while making the booking.",
      "Additional guests may be subject to:",
    ],
    bullets: [
      "Additional charges",
      "Extra-bed charges",
      "Hotel approval",
      "Room availability",
    ],
    after: [
      "The hotel may refuse accommodation where the number of guests exceeds the permitted occupancy.",
    ],
  },
  {
    number: 9,
    title: "Child Policy",
    paragraphs: [
      "Child policies vary from hotel to hotel.",
      "Depending on the property, additional charges may apply for:",
    ],
    bullets: [
      "Children",
      "Extra beds",
      "Baby cots",
      "Meals",
      "Breakfast",
      "Additional occupancy",
    ],
    after: [
      "Customers should review the applicable child policy before confirming the booking.",
    ],
  },
  {
    number: 10,
    title: "Meals and Meal Plans",
    paragraphs: [
      "If breakfast, lunch, dinner or another meal plan is included, it will be mentioned in the applicable booking confirmation.",
      "Meal inclusions may vary according to the hotel's policy.",
      "Meal timings are determined by the hotel.",
      "Special meal requests are subject to hotel availability and confirmation.",
    ],
  },
  {
    number: 11,
    title: "Hotel Facilities and Services",
    paragraphs: [
      "Hotel facilities and services may vary and can be subject to availability.",
      "Facilities such as:",
    ],
    bullets: [
      "Swimming pool",
      "Gym",
      "Spa",
      "Restaurant",
      "Wi-Fi",
      "Parking",
      "Room service",
      "Airport transfer",
    ],
    after: [
      "may be subject to hotel operating hours, maintenance, seasonal availability or additional charges.",
      "MakeBeeTrip does not guarantee the availability of every hotel facility unless specifically confirmed in the booking.",
    ],
  },
  {
    number: 12,
    title: "Hotel Taxes and Additional Charges",
    paragraphs: [
      "Certain taxes, tourism fees, resort fees, city taxes, security deposits or other charges may be payable separately at the hotel, depending on the destination and property.",
      "Customers should review the booking details for information regarding included and excluded charges.",
    ],
  },
  {
    number: 13,
    title: "Security Deposit",
    paragraphs: [
      "Some hotels may require a refundable or non-refundable security deposit at check-in.",
      "Such deposits are determined by the hotel and are separate from the amount paid to MakeBeeTrip unless otherwise stated.",
      "The hotel may use the deposit to cover applicable incidental charges, damages or other amounts according to its policies.",
    ],
  },
  {
    number: 14,
    title: "Hotel Cancellation",
    paragraphs: [
      "Hotel cancellation is subject to the cancellation policy applicable to the specific booking.",
      "Depending on the selected rate, a booking may be:",
    ],
    bullets: [
      "Free cancellation before a specified deadline",
      "Partially refundable",
      "Subject to cancellation charges",
      "Completely non-refundable",
    ],
    after: [
      "The cancellation conditions applicable to the booking will be communicated or displayed during the booking process.",
    ],
  },
  {
    number: 15,
    title: "Non-Refundable Hotel Bookings",
    paragraphs: [
      "Certain hotel rates are offered on a Non-Refundable basis.",
      "Once confirmed, such bookings may not be eligible for cancellation or refund except where permitted by the hotel, supplier or applicable law.",
      "Customers should carefully review the cancellation policy before making payment.",
    ],
  },
  {
    number: 16,
    title: "Hotel No-Show",
    paragraphs: [
      "If a guest does not arrive at the hotel on the scheduled check-in date without cancelling the booking according to the applicable cancellation policy, the booking may be treated as a No-Show.",
      "A no-show may result in:",
    ],
    bullets: [
      "Loss of the booking amount",
      "Cancellation of the remaining stay",
      "No refund",
      "Additional charges",
    ],
    after: [
      "The applicable hotel or supplier policy will determine the outcome.",
    ],
  },
  {
    number: 17,
    title: "Booking Amendments",
    paragraphs: [
      "Requests to change a hotel booking are subject to availability and the hotel's or supplier's rules.",
      "Changes may include:",
    ],
    bullets: [
      "Check-in date",
      "Check-out date",
      "Number of guests",
      "Room category",
      "Meal plan",
      "Guest details",
    ],
    after: [
      "Additional charges or fare/rate differences may apply.",
      "Some hotel bookings may not permit amendments.",
    ],
  },
  {
    number: 18,
    title: "Hotel Changes or Overbooking",
    paragraphs: [
      "In exceptional circumstances, a hotel or supplier may be unable to provide the originally booked accommodation due to overbooking, operational issues or other circumstances.",
      "Where this occurs, MakeBeeTrip may assist with the alternative arrangements offered by the hotel or supplier.",
      "Any alternative accommodation will be subject to availability and the applicable supplier policy.",
    ],
  },
  {
    number: 19,
    title: "Hotel Standards and Ratings",
    paragraphs: [
      "Hotel classifications, ratings and star categories may be based on the hotel's destination, local classification system, supplier information or other applicable standards.",
      "Hotel ratings may differ between destinations and booking platforms.",
      "MakeBeeTrip does not independently guarantee the quality or classification of every hotel listed on the platform.",
    ],
  },
  {
    number: 20,
    title: "Special Requests",
    paragraphs: [
      "Special requests such as:",
    ],
    bullets: [
      "Connecting rooms",
      "Specific floor",
      "Bed preference",
      "Early check-in",
      "Late check-out",
      "Room view",
      "Extra bed",
      "Baby cot",
    ],
    after: [
      "are subject to hotel availability and confirmation.",
      "A special request does not constitute a guaranteed service unless specifically confirmed by the hotel.",
    ],
  },
  {
    number: 21,
    title: "Hotel Payment",
    paragraphs: [
      "Hotel bookings may be paid through MakeBeeTrip's available payment methods and third-party payment gateway providers.",
      "Depending on the booking conditions, some charges may be payable directly at the hotel.",
      "Customers should review the booking confirmation carefully to understand whether any amount is payable at the property.",
    ],
  },
  {
    number: 22,
    title: "Hotel Property Rules",
    paragraphs: [
      "Guests must comply with the hotel's rules and policies during their stay.",
      "These may include rules relating to:",
    ],
    bullets: [
      "Smoking",
      "Pets",
      "Visitors",
      "Noise",
      "Alcohol",
      "Identification",
      "Security deposits",
      "Property use",
      "Damages",
      "Check-in and check-out",
    ],
    after: [
      "The hotel may take action in accordance with its policies if these rules are violated.",
    ],
  },
  {
    number: 23,
    title: "Hotel Information",
    paragraphs: [
      "Hotel descriptions, photographs, facilities, room information, ratings and other property details may be provided by hotels, suppliers or other third parties.",
      "We make reasonable efforts to display relevant information accurately, but hotel information may change.",
      "Customers should refer to the booking confirmation and applicable hotel terms for the final booking details.",
    ],
  },
  {
    number: 24,
    title: "Limitation of Responsibility",
    paragraphs: [
      "MakeBeeTrip facilitates hotel bookings but does not directly control the operation of every hotel listed on the platform.",
      "To the extent permitted by applicable law, MakeBeeTrip shall not be responsible for matters outside its reasonable control, including:",
    ],
    bullets: [
      "Hotel operational issues",
      "Temporary closure of facilities",
      "Maintenance",
      "Hotel staff conduct",
      "Local disruptions",
      "Changes in hotel facilities",
      "Overbooking by the hotel",
      "Circumstances beyond the reasonable control of MakeBeeTrip or the hotel",
    ],
    after: [
      "MakeBeeTrip will make reasonable efforts to assist customers where applicable.",
    ],
  },
  {
    number: 25,
    title: "Changes to Hotel Booking Terms",
    paragraphs: [
      'MakeBeeTrip may update these Hotel Booking Terms & Conditions from time to time to reflect changes in our services, supplier policies or applicable requirements.',
      'The updated version will be published on this page with a revised "Last Updated" date.',
    ],
  },
];

const TermsSection = ({ section }) => (
  <section
    id={`hotel-section-${section.number}`}
    className="mbt-hotel-section"
  >
    <div className="mbt-hotel-section__heading">
      <span className="mbt-hotel-section__number">
        {String(section.number).padStart(2, "0")}
      </span>

      <h2>{section.title}</h2>
    </div>

    <div className="mbt-hotel-section__content">
      {section.paragraphs?.map((paragraph, index) => (
        <p key={`paragraph-${index}`}>{paragraph}</p>
      ))}

      {section.bullets && (
        <ul>
          {section.bullets.map((bullet, index) => (
            <li key={`bullet-${index}`}>{bullet}</li>
          ))}
        </ul>
      )}

      {section.after?.map((paragraph, index) => (
        <p key={`after-${index}`}>{paragraph}</p>
      ))}
    </div>
  </section>
);

const HotelTerms = () => {
  usePageMeta(
  "Hotel Booking Terms & Conditions | MakeBeeTrip",
  "Terms for hotel bookings on MakeBeeTrip: rates, check-in policies, amendments, cancellations and hotel-side conditions.",
  "hotel booking terms, hotel terms and conditions, hotel cancellation policy, check in policy, room booking terms"
);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const documentHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      const progress =
        documentHeight > 0 ? (scrollTop / documentHeight) * 100 : 0;

      setScrollProgress(Math.min(progress, 100));
      setShowScrollTop(scrollTop > 500);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <main className="mbt-hotel-terms">
      <div
        className="mbt-hotel-scroll-progress"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      <nav className="mbt-hotel-sticky-nav">
        <div className="mbt-hotel-nav-container">
          <button
            type="button"
            className="mbt-hotel-back-button"
            onClick={() => window.history.back()}
          >
            <span aria-hidden="true">←</span>
            Back
          </button>
        </div>
      </nav>

      <header className="mbt-hotel-hero">
        <div className="mbt-hotel-hero__container">
          <div className="mbt-hotel-icon" aria-hidden="true">
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 21h18" />
    <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
    <path d="M9 7h1" />
    <path d="M14 7h1" />
    <path d="M9 11h1" />
    <path d="M14 11h1" />
    <path d="M9 15h1" />
    <path d="M14 15h1" />
    <path d="M10 21v-3h4v3" />
  </svg>
</div>

          <span className="mbt-hotel-eyebrow">
            SERVICE-SPECIFIC TERMS
          </span>

          <h1>Hotel Booking Terms &amp; Conditions</h1>

          <p className="mbt-hotel-hero__description">
            Please read these Hotel Booking Terms &amp; Conditions carefully
            before searching, booking or purchasing hotel accommodation through
            MakeBeeTrip.
          </p>

          <div className="mbt-hotel-updated-badge">
            Last Updated: 24 September 2026
          </div>
        </div>
      </header>

      <div className="mbt-hotel-content-container">
        <article className="mbt-hotel-card">
          <div className="mbt-hotel-document-intro">
            <p>
              These Hotel Booking Terms &amp; Conditions apply to hotel and
              accommodation bookings made through{" "}
              <strong>MakeBeeTrip Private Limited</strong>.
            </p>

            <p>
              By searching, booking or purchasing hotel accommodation through{" "}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://makebeetrip.com/
              </a>
              , you acknowledge that you have read and agreed to these terms.
            </p>
          </div>

          <div
            className="mbt-hotel-quick-nav"
            aria-label="Hotel terms sections"
          >
            <div className="mbt-hotel-quick-nav__header">
              <span className="mbt-hotel-quick-nav__eyebrow">
                QUICK NAVIGATION
              </span>

              <h2>Hotel Terms</h2>

              <p>
                Jump directly to any section of the Hotel Booking Terms.
              </p>
            </div>

            <div className="mbt-hotel-quick-nav__links">
              {hotelTerms.map((section) => (
                <a
                  key={section.number}
                  href={`#hotel-section-${section.number}`}
                >
                  <span>
                    {String(section.number).padStart(2, "0")}
                  </span>
                  {section.title}
                </a>
              ))}

              <a href="#hotel-important-notice">
                <span>!</span>
                Important Notice
              </a>
            </div>
          </div>

          <div className="mbt-hotel-sections">
            {hotelTerms.map((section, index) => (
              <React.Fragment key={section.number}>
                <TermsSection section={section} />

                {index < hotelTerms.length - 1 && (
                  <hr className="mbt-hotel-divider" />
                )}
              </React.Fragment>
            ))}
          </div>

          <hr className="mbt-hotel-divider" />

          <section
            id="hotel-important-notice"
            className="mbt-hotel-important-notice"
          >
            <div
              className="mbt-hotel-important-notice__icon"
              aria-hidden="true"
            >
              !
            </div>

            <div>
              <span>IMPORTANT NOTICE</span>

              <p>
                All hotel bookings are subject to the specific hotel's and/or
                supplier's availability, terms, conditions, cancellation
                policy, check-in policy and other applicable rules. Customers
                are advised to carefully review the booking details and
                cancellation conditions before completing payment.
              </p>
            </div>
          </section>

          <footer className="mbt-hotel-footer">
  <div className="mbt-hotel-footer__inner">
    <p className="mbt-hotel-footer__tagline">
  MakeBeeTrip – Your Journey, Our Commitment.
</p>

    <div className="mbt-hotel-footer__contact">
      <a href="tel:9355471999">
        9355471999
      </a>

      <span aria-hidden="true">•</span>

      <a href="mailto:Holidays@makebeetrip.com">
        Holidays@makebeetrip.com
      </a>

      <span aria-hidden="true">•</span>

      <a
        href="https://makebeetrip.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        makebeetrip.com
      </a>
    </div>

    <p className="mbt-hotel-footer__copyright">
      © 2026 MakeBeeTrip Private Limited. All rights reserved.
    </p>
  </div>
</footer>
        </article>
      </div>

      {showScrollTop && (
        <button
          type="button"
          className="mbt-hotel-scroll-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </main>
  );
};

export default HotelTerms;